import { themeZafire } from '@zafire/highcharts';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { INCOME_EXPENSE_INPUT_DATE_FORMAT } from '../../../app/config/constants';
import { getDateString } from '../../../app/utils/date-utils';
import { LIQUIDITY_NEED_DETAILS_CHART_HEIGHT_PX, LIQUIDITY_NEED_DETAILS_DEFAULT_VISIBLE_COLUMNS } from '../../config/constants';
import { useLiquidityChartAutoSize } from '../../hooks';
import type { ILiquidityCalculationValues } from '../../interfaces';
import { createTooltipFormatter, formatMillions, liquidityNeedTooltipPositioner } from '../../utils';

Highcharts.setOptions(themeZafire.create());

const CASHFLOW_INFLOW_COLOR = 'var(--bg-color-success-solid-normal)';
const CASHFLOW_OUTFLOW_COLOR = 'var(--bg-color-danger-solid-normal)';
const CASHFLOW_NEUTRAL_COLOR = 'var(--color-data-viz-primary-normal)';

const cashflowColumnColor = (amount: number): string => {
    if (amount > 0) return CASHFLOW_INFLOW_COLOR;
    if (amount < 0) return CASHFLOW_OUTFLOW_COLOR;

    return CASHFLOW_NEUTRAL_COLOR;
};

interface ILiquidityNeedDetailsGraphProps {
    calculationData: ILiquidityCalculationValues;
    visibleColumns?: number;
}

export const LiquidityNeedDetailsGraph: FunctionComponent<ILiquidityNeedDetailsGraphProps> = ({
    calculationData,
    visibleColumns = LIQUIDITY_NEED_DETAILS_DEFAULT_VISIBLE_COLUMNS,
}) => {
    const { t } = useTranslation();
    const { chartRef, containerRef, chartHeight, containerWidth } = useLiquidityChartAutoSize(LIQUIDITY_NEED_DETAILS_CHART_HEIGHT_PX);
    const { currency, needCalculationSteps } = calculationData;

    // Fit `visibleColumns` columns into the container; once there are more, widen the plot area so it scrolls.
    const isScrollable = containerWidth > 0 && needCalculationSteps.length > visibleColumns;
    const scrollableMinWidth = (needCalculationSteps.length / visibleColumns) * containerWidth;

    const options = useMemo<Highcharts.Options>(() => {
        // One column per month; label the year on the first month of each year and draw a separator at year boundaries.
        const yearByIndex = needCalculationSteps.map((step) => step.month?.slice(0, 4) ?? '');
        const categories = yearByIndex.map((year, index) => (index === 0 || year !== yearByIndex[index - 1] ? year : ''));
        const yearSeparators: Highcharts.XAxisPlotLinesOptions[] = yearByIndex
            .map((year, index) => (index > 0 && year !== '' && year !== yearByIndex[index - 1] ? index : -1))
            .filter((index) => index >= 0)
            .map((index) => ({ value: index - 0.5, color: 'var(--border-color-default-normal)', width: 1, zIndex: 3 }));

        return {
            chart: {
                type: 'column',
                backgroundColor: 'var(--bg-color-default-normal)',
                height: chartHeight,
                spacing: [24, 16, 16, 16],
                scrollablePlotArea: isScrollable ? { minWidth: scrollableMinWidth, scrollPositionX: 0, opacity: 1 } : undefined,
            },
            accessibility: { enabled: false },
            credits: { enabled: false },
            exporting: { enabled: false },
            title: { text: '' },
            legend: { enabled: true },
            tooltip: {
                backgroundColor: '#ffffff',
                borderWidth: 0,
                borderRadius: 4,
                padding: 8,
                useHTML: true,
                // Keep the tooltip inside the chart's SVG container. Highcharts otherwise renders it out to <body> when
                // the plot area is scrollable, which puts it behind the top-layer (showModal) dialog that hosts this
                // graph. Rendered inside, it shares the dialog's top layer, so it's above the modal.
                outside: false,
                // Clamp the tooltip into the currently visible plot window so it isn't clipped by the scrollable plot
                // area at the outermost columns (see liquidityNeedTooltipPositioner).
                positioner: liquidityNeedTooltipPositioner,
                formatter: createTooltipFormatter(t, currency),
            },
            xAxis: {
                type: 'category',
                categories,
                lineColor: 'var(--border-color-default-normal)',
                tickLength: 0,
                gridLineWidth: 0,
                plotLines: yearSeparators,
                labels: {
                    style: {
                        color: 'var(--fg-color-default-normal)',
                        fontSize: 'var(--type-body-medium-font-size)',
                        fontWeight: 'var(--type-body-medium-regular-font-weight)',
                        lineHeight: 'var(--type-body-medium-line-height)',
                    },
                },
            },
            yAxis: {
                title: { text: t('liquidityCalculatorGraph.yAxisLabel', { currency }) },
                gridLineColor: 'var(--border-color-default-normal)',
                gridLineDashStyle: 'Dot',
                gridLineWidth: 1,
                plotLines: [{ value: 0, color: 'var(--border-color-default-emphasis)', width: 2, zIndex: 4 }],
                labels: {
                    // eslint-disable-next-line react-hooks/unsupported-syntax -- Highcharts axis label formatter API
                    formatter: function (this) {
                        return formatMillions(this.value as number);
                    },
                    style: { color: 'var(--zf-charts-general-axis-title)' },
                },
            },
            plotOptions: {
                series: { stickyTracking: false },
                columnrange: { borderWidth: 0, pointPadding: 0.1, minPointLength: 2 },
                line: {
                    marker: { enabled: true, radius: 4 },
                    dataLabels: {
                        enabled: true,
                        verticalAlign: 'top',
                        y: 4,
                        formatter: function (this) {
                            return formatMillions(Math.abs((this.y ?? 0) as number));
                        },
                        style: {
                            color: 'var(--fg-color-default-emphasis)',
                            fontSize: '12px',
                            fontWeight: '600',
                            textOutline: 'none',
                        },
                    },
                },
            },
            series: [
                {
                    type: 'columnrange',
                    name: t('liquidityNeedDetailsGraph.series.cashflows'),
                    data: needCalculationSteps.map((step) => {
                        const monthLabel = getDateString(step.month, INCOME_EXPENSE_INPUT_DATE_FORMAT);

                        // Every month renders a (possibly flat, minPointLength) bar so it is a hover target; empty
                        // months keep the balance flat (open === close) and surface a "No cashflows" tooltip.
                        return {
                            name: monthLabel,
                            low: Math.min(step.open, step.close),
                            high: Math.max(step.open, step.close),
                            color: cashflowColumnColor(step.amount),
                            custom: {
                                date: monthLabel,
                                amount: step.amount,
                                balance: step.close,
                                cashFlows: (step.cashFlows ?? []).map((cashFlow) => ({
                                    type: cashFlow.type,
                                    name: cashFlow.name,
                                    amount: cashFlow.amount,
                                })),
                            },
                        };
                    }),
                },
                {
                    id: 'liquidityNeed',
                    type: 'line',
                    name: t('liquidityNeedDetailsGraph.series.liquidityNeed'),
                    color: 'var(--color-data-viz-alternative-one-bold)',
                    lineWidth: 2,
                    data: needCalculationSteps.map((step) => ({
                        y: step.need,
                        custom: { date: getDateString(step.month, INCOME_EXPENSE_INPUT_DATE_FORMAT) },
                    })),
                },
            ],
        };
    }, [t, currency, needCalculationSteps, isScrollable, scrollableMinWidth, chartHeight]);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <HighchartsReact
                key={visibleColumns}
                ref={chartRef}
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: '100%', height: '100%' } }}
            />
        </div>
    );
};
